/* ============================================================================
   People directory — a studio-wide contacts book of everyone ever added to a
   project (contractors, consultants, clients, site teams). Remembered as work
   happens so the same person never has to be re-typed on the next project:
   pick them and their contact + role fill in. Synced as one "meta" aggregate
   so the whole studio shares it, on every device.
   ========================================================================== */
import { useEffect, useState } from "react";
import { uid } from "./store";

export interface PersonEntry {
  id: string;
  name: string;
  company: string;
  email?: string;
  phone?: string;
  roles: string[];         // roles they've held — contractor, consultant, client…
  /** Company logo (data URI) — remembered so re-appointing this contractor on a
      new project brings their logo with them. */
  logo?: string;
  useCount: number;
  lastUsedAt: string;
  addedAt: string;
}

const KEY = "atelier.people.v1";
export const PEOPLE_AGG_ID = "people";

type State = { people: PersonEntry[] };

function load(): State {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return { people: [] };
}

let state: State = load();
const subs = new Set<() => void>();

function commit(next: State) {
  state = next;
  try { localStorage.setItem(KEY, JSON.stringify(state)); } catch { /* quota */ }
  subs.forEach((f) => f());
  changeListener?.(asAggregate());
}

export function usePeople(): PersonEntry[] {
  const [, force] = useState(0);
  useEffect(() => {
    const f = () => force((n) => n + 1);
    subs.add(f);
    return () => { subs.delete(f); };
  }, []);
  return state.people;
}
export function getPeople() { return state.people; }

const norm = (s?: string) => (s ?? "").trim().toLowerCase();

/** Find an existing person — by email if we have one, else by name+company. */
function findIndex(name: string, company: string, email?: string): number {
  if (email && norm(email)) {
    const i = state.people.findIndex((p) => norm(p.email) === norm(email));
    if (i >= 0) return i;
  }
  return state.people.findIndex((p) => norm(p.name) === norm(name) && norm(p.company) === norm(company));
}

/* --- sync hooks --------------------------------------------------------- */
export type PeopleAggregate = { id: string; people: PersonEntry[] };
function asAggregate(): PeopleAggregate { return { id: PEOPLE_AGG_ID, people: state.people }; }
let changeListener: ((a: PeopleAggregate) => void) | null = null;
export function onPeopleChange(f: (a: PeopleAggregate) => void) { changeListener = f; }
export function getPeopleAggregates(): PeopleAggregate[] { return [asAggregate()]; }
export function replacePeopleAggregates(aggs: { id: string }[]) {
  const row = aggs.find((a) => a.id === PEOPLE_AGG_ID) as PeopleAggregate | undefined;
  if (!row) return;
  state = { people: row.people ?? [] };
  try { localStorage.setItem(KEY, JSON.stringify(state)); } catch { /* quota */ }
  subs.forEach((f) => f());
}

/* --- actions ------------------------------------------------------------ */
export const peopleActions = {
  /** Learn or refresh a person. Upserts by email / name+company, merges the
      contact details, records the role and bumps recency. */
  remember(data: { name: string; company: string; email?: string; phone?: string; role?: string; logo?: string }): PersonEntry {
    const name = data.name.trim();
    const company = data.company.trim();
    const now = new Date().toISOString();
    const i = findIndex(name, company, data.email);
    if (i >= 0) {
      const e = state.people[i];
      const next: PersonEntry = {
        ...e,
        name: name || e.name,
        company: company || e.company,
        email: data.email?.trim() || e.email,
        phone: data.phone?.trim() || e.phone,
        logo: data.logo || e.logo,
        roles: data.role && !e.roles.includes(data.role) ? [...e.roles, data.role] : e.roles,
        useCount: e.useCount + 1,
        lastUsedAt: now,
      };
      commit({ people: state.people.map((x, j) => (j === i ? next : x)) });
      return next;
    }
    const entry: PersonEntry = {
      id: uid("pp-"), name, company,
      email: data.email?.trim() || undefined, phone: data.phone?.trim() || undefined,
      logo: data.logo || undefined,
      roles: data.role ? [data.role] : [], useCount: 1, lastUsedAt: now, addedAt: now,
    };
    commit({ people: [entry, ...state.people] });
    return entry;
  },
  update(id: string, patch: Partial<Pick<PersonEntry, "name" | "company" | "email" | "phone" | "roles" | "logo">>) {
    commit({ people: state.people.map((p) => (p.id === id ? { ...p, ...patch } : p)) });
  },
  /** Remember a company's logo against everyone on record for that company. */
  setCompanyLogo(company: string, logo: string | undefined) {
    const c = norm(company);
    if (!c) return;
    commit({ people: state.people.map((p) => (norm(p.company) === c ? { ...p, logo: logo || p.logo } : p)) });
  },
  remove(id: string) {
    commit({ people: state.people.filter((p) => p.id !== id) });
  },
};

/** The logo on record for a company (any contact), for reuse on a new project. */
export function companyLogo(company: string): string | undefined {
  const c = norm(company);
  return state.people.find((p) => norm(p.company) === c && p.logo)?.logo;
}
/** Distinct companies in the directory (most-recent first) — the registrar. */
export function sortedCompanies(role?: string): { company: string; logo?: string; contact?: PersonEntry }[] {
  const seen = new Map<string, { company: string; logo?: string; contact?: PersonEntry }>();
  for (const p of sortedPeople(role)) {
    const key = norm(p.company);
    if (!key || seen.has(key)) continue;
    seen.set(key, { company: p.company, logo: p.logo, contact: p });
  }
  return [...seen.values()];
}

/** Most-recently-used first — for the pickers. */
export function sortedPeople(role?: string): PersonEntry[] {
  return [...state.people]
    .filter((p) => !role || p.roles.includes(role) || p.roles.length === 0)
    .sort((a, b) => b.lastUsedAt.localeCompare(a.lastUsedAt));
}
